import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { FaArrowLeft, FaHeart, FaShieldAlt, FaGavel } from 'react-icons/fa';
import Logo from './Logo';

const TermsOfUse = () => {
  const navigate = useNavigate();
  usePageTitle('Terms of Use');
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 flex-col">
              <div className="text-center lg:text-left">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-32 mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Terms of Use
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Last Updated: December 1, 2025
            </p>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div
          variants={containerVariants}
          className="container mx-auto px-4 pb-16 max-w-5xl"
        >
          <motion.div
            variants={itemVariants}
            className="glass rounded-3xl p-8 md:p-12 max-w-4xl mx-auto relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink opacity-5" />
            
            <div className="relative z-10 prose prose-invert prose-lg max-w-none">
              <div className="text-gray-300 leading-relaxed space-y-6">
                <h2 className="text-2xl font-bold text-romantic-pink">1. Acceptance of Terms</h2>
                <p>
                  By downloading, registering, or using 'datenite', you agree to these Terms & Conditions, our Privacy Policy, and our Cookie Policy.
                </p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">2. Eligibility</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You must be 18 years or older.</li>
                  <li>You must create your account with accurate information.</li>
                  <li>You cannot use 'datenite' if you are suspended or banned.</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">3. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the app respectfully</li>
                  <li>Not impersonate others</li>
                  <li>Not post harmful, illegal, or explicit content</li>
                  <li>Not harass, stalk, or harm other users</li>
                  <li>Follow our safety rules</li>
                  <li>Not attempt to hack, scrape, or misuse our systems</li>
                </ul>
                <p className="text-romantic-pink font-medium">Violation may lead to account termination.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">4. Account Access & Security</h2>
                <p>You are responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintaining your login confidentiality</li>
                  <li>Reporting suspicious activity</li>
                  <li>Ensuring your device security</li>
                </ul>
                <p>We may suspend accounts for security reasons.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">5. In-App Interactions</h2>
                <p>'datenite' is not responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Matches</li>
                  <li>Conversations</li>
                  <li>User decisions</li>
                  <li>Offline meetings</li>
                </ul>
                <p>Users must take their own precautions when meeting others.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">6. Content You Upload</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You grant 'datenite' a non-exclusive, royalty-free licence to display your uploaded content within the app for matchmaking.</li>
                  <li>You are liable for the content you publish.</li>
                  <li>We may remove content that violates guidelines.</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">7. Subscription & Payment Terms</h2>
                <h3 className="text-xl font-semibold text-white mt-4">A. Auto-Renewal</h3>
                <p>All premium subscriptions:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Automatically renew</li>
                  <li>Are billed via App Store / Google Play Store</li>
                  <li>Must be cancelled through your store account</li>
                </ul>
                <h3 className="text-xl font-semibold text-white mt-4">B. Refunds</h3>
                <p>Refunds are handled by Apple or Google according to their policies.</p>
                <h3 className="text-xl font-semibold text-white mt-4">C. Pricing</h3>
                <p>Pricing may vary by region and may change with notice.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">8. Termination of Account</h2>
                <p>We may suspend or terminate accounts for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Safety violations</li>
                  <li>Illegal activity</li>
                  <li>Fake profiles</li>
                  <li>Harassment</li>
                  <li>Spam or scam activity</li>
                </ul>
                <p>Users may delete accounts anytime.</p>
                <p>You may send Delete request from App itself anytime you want. Once the account is deleted.. all the Data, Subscription plan associated with your account will be null and Void. You cannot create new account with same login credentials.   </p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">9. Location-Based Features</h2>
                <p>You acknowledge that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The app provides location-based matchmaking</li>
                  <li>You must grant permission to use these features</li>
                  <li>Turning off location may limit functionality</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">10. Intellectual Property</h2>
                <p>All content, branding, code, and design belong to 'datenite'.</p>
                <p>You cannot copy, reverse-engineer, or reuse our assets.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">11. Liability Disclaimer</h2>
                <p>'datenite' is provided "as is".</p>
                <p>We do not guarantee:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Matches</li>
                  <li>Relationship success</li>
                  <li>Availability of services</li>
                  <li>Error-free operation</li>
                </ul>
                <p>We are not liable for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>User interactions</li>
                  <li>Data loss</li>
                  <li>Offline meetings</li>
                  <li>Third-party actions</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">12. Third-Party Services</h2>
                <p>By using 'datenite', you also agree to the terms of:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Google</li>
                  <li>Apple</li>
                  <li>Firebase</li>
                  <li>Payment gateways</li>
                  <li>Analytics tools</li>
                  <li>Agora</li>
                </ul>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">13. Safety Policy</h2>
                <p>Users must follow our safety guidelines.</p>
                <p>'datenite' may moderate, remove, or act against harmful users.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">14. Changes to These Terms</h2>
                <p>We may update Terms occasionally. Continued use means acceptance.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">15. Governing Law</h2>
                <p>These terms are governed by the laws of the United Kingdom.</p>

                <h2 className="text-2xl font-bold text-romantic-pink mt-8">16. Contact</h2>
                <p>For legal inquiries:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>📧 support@datenite.co.uk</li>
                  <li>🌐 <a href="https://datenite.co.uk" className="text-romantic-pink hover:underline">https://datenite.co.uk</a></li>
                </ul>

                <p className="mt-8">
                  If you have any questions concerning these Terms of Use, please contact us at <a href="mailto:support@datenite.co.uk" className="text-romantic-pink hover:underline">support@datenite.co.uk</a>.
                </p>

                
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfUse;
